import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "typeButtons", "httpConfig", "tcpConfig", "udpConfig", "methodSelect", "intervalSelect",
    "timeoutSelect", "redirectCheck", "headersContainer", "hiddenField",
    "newHeaderRow", "headerSelect", "headerValue", "customHeaderInput", "tcpMessageInput", "udpMessageInput",
    "monitorUptimeCheck", "monitorSslCheck",
    "dnsRecordTypeSelect", "dnsExpectedValueInput", "dnsNameserverInput", "dnsTimeoutSelect"
  ]
  static values = { structure: Object }

  // DNS record type constants (must match Check::DnsRecordType in Ruby)
  static DNS_RECORD_TYPE = {
    A: "A",
    AAAA: "AAAA",
    CNAME: "CNAME",
    MX: "MX",
    TXT: "TXT",
    NS: "NS",
    SOA: "SOA"
  }

  connect() {
    this.config = this.structureValue || this.defaultConfig()

    // Ensure required fields are set for the current type
    // This is important when navigating directly to a specific type via URL param
    this.ensureRequiredFieldsForType(this.config.request_type)

    this.renderUI()
    this.updateHiddenField() // Update hidden field with complete config

    // Emit initial request type after a short delay to ensure conditions controller is connected
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('request-type-changed', {
        detail: { requestType: this.config.request_type },
        bubbles: false
      }))
    }, 100)
  }

  defaultConfig() {
    return {
      request_type: "http",
      http_method: "GET",
      interval: 300,
      timeout_ms: 10000, // Milliseconds for consistency with Rails
      verify_ssl: true,
      follow_redirects: true,
      custom_headers: {},
      monitor_types: ["uptime", "ssl"]
    }
  }

  ensureRequiredFieldsForType(type) {
    const defaults = this.defaultConfig()

    // For HTTP/BROWSER types, ensure http_method is present
    if (["http", "browser"].includes(type)) {
      if (!this.config.http_method) {
        this.config.http_method = defaults.http_method
      }
      if (this.config.verify_ssl === undefined) {
        this.config.verify_ssl = defaults.verify_ssl
      }
      if (this.config.follow_redirects === undefined) {
        this.config.follow_redirects = defaults.follow_redirects
      }
      if (!this.config.custom_headers) {
        this.config.custom_headers = defaults.custom_headers
      }
    }

    // For DNS type, ensure record_type is present and timeout_ms is in milliseconds
    if (type === "dns") {
      if (!this.config.record_type) {
        this.config.record_type = this.constructor.DNS_RECORD_TYPE.A
      }
      // DNS timeout_ms should be in milliseconds (1000-30000)
      if (!this.config.timeout_ms || this.config.timeout_ms < 1000) {
        this.config.timeout_ms = 5000
      }
    }

    // Ensure interval and timeout_ms are always present
    if (!this.config.interval) {
      this.config.interval = defaults.interval
    }
    // For all types, ensure timeout_ms is present
    if (!this.config.timeout_ms) {
      this.config.timeout_ms = defaults.timeout_ms
    }
  }

  selectType(event) {
    const type = event.currentTarget.dataset.type
    this.config.request_type = type

    // Ensure required fields are present for the selected type
    this.ensureRequiredFieldsForType(type)

    this.updateTypeButtons(type)
    this.toggleConfigSections(type)
    this.renderUI() // Re-render UI to reflect any new field values
    this.updateHiddenField()

    // Notify the conditions controller about the request type change
    document.dispatchEvent(new CustomEvent('request-type-changed', {
      detail: { requestType: type },
      bubbles: false
    }))
  }

  updateMethod(event) {
    this.config.http_method = event.target.value
    this.updateHiddenField()
  }

  updateInterval(event) {
    this.config.interval = parseInt(event.target.value)
    this.updateHiddenField()
  }

  updateTimeout(event) {
    // Convert seconds to milliseconds (select values are in seconds)
    this.config.timeout_ms = parseInt(event.target.value) * 1000
    this.updateHiddenField()
  }

  updateRedirects(event) {
    this.config.follow_redirects = event.target.checked
    this.updateHiddenField()
  }

  updateTcpMessage(event) {
    this.config.tcp_message = event.target.value
    this.updateHiddenField()
  }

  updateUdpMessage(event) {
    this.config.udp_message = event.target.value
    this.updateHiddenField()
  }

  updateDnsRecordType(event) {
    this.config.record_type = event.target.value
    this.updateHiddenField()
  }

  updateDnsExpectedValue(event) {
    const value = event.target.value.trim()
    if (value) {
      this.config.expected_value = value
    } else {
      delete this.config.expected_value
    }
    this.updateHiddenField()
  }

  updateDnsNameserver(event) {
    const value = event.target.value.trim()
    if (value) {
      this.config.nameserver = value
    } else {
      delete this.config.nameserver
    }
    this.updateHiddenField()
  }

  updateDnsTimeout(event) {
    this.config.timeout_ms = parseInt(event.target.value)
    this.updateHiddenField()
  }

  updateMonitorTypes(event) {
    // Collect checked monitor types
    const monitorTypes = []

    if (this.hasMonitorUptimeCheckTarget && this.monitorUptimeCheckTarget.checked) {
      monitorTypes.push("uptime")
    }

    if (this.hasMonitorSslCheckTarget && this.monitorSslCheckTarget.checked) {
      monitorTypes.push("ssl")
    }

    // Ensure at least one type is selected
    if (monitorTypes.length === 0) {
      // Prevent unchecking the last checkbox
      event.target.checked = true
      return
    }

    this.config.monitor_types = monitorTypes
    this.updateHiddenField()
  }

  updateHeaderSelect(event) {
    const selectedValue = event.target.value

    if (selectedValue === "custom") {
      // Show the custom header input field
      if (this.hasCustomHeaderInputTarget) {
        this.customHeaderInputTarget.style.display = "block"
        this.customHeaderInputTarget.focus()
      }
    } else {
      // Hide the custom header input field
      if (this.hasCustomHeaderInputTarget) {
        this.customHeaderInputTarget.style.display = "none"
        this.customHeaderInputTarget.value = ""
      }
    }
  }

  addHeader() {
    if (!this.hasHeaderSelectTarget || !this.hasHeaderValueTarget) return

    let headerKey = this.headerSelectTarget.value.trim()
    const headerValue = this.headerValueTarget.value.trim()

    // If custom is selected, use the custom header input value
    if (headerKey === "custom" && this.hasCustomHeaderInputTarget) {
      headerKey = this.customHeaderInputTarget.value.trim()
    }

    if (headerKey && headerValue && headerKey !== "custom") {
      this.config.custom_headers[headerKey] = headerValue
      this.renderHeaders()
      this.updateHiddenField()

      // Clear the form
      this.clearHeader()
    } else {
      // Show validation message without using alert
      this.showValidationMessage("Please select a header and enter a value")
    }
  }

  showValidationMessage(message) {
    // Create a temporary validation message
    const existingMessage = this.element.querySelector('.header-validation-message')
    if (existingMessage) {
      existingMessage.remove()
    }

    const messageElement = document.createElement('div')
    messageElement.className = 'alert alert-warning alert-dismissible fade show header-validation-message mt-2'
    messageElement.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `

    // Insert after the add header button
    const addButton = this.element.querySelector('[data-action="click->request-config#addHeader"]')
    addButton.parentNode.insertBefore(messageElement, addButton.nextSibling)

    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      if (messageElement && messageElement.parentNode) {
        messageElement.remove()
      }
    }, 3000)
  }

  clearHeader() {
    if (this.hasHeaderSelectTarget) {
      this.headerSelectTarget.value = ""
    }
    if (this.hasHeaderValueTarget) {
      this.headerValueTarget.value = ""
    }
    if (this.hasCustomHeaderInputTarget) {
      this.customHeaderInputTarget.value = ""
      this.customHeaderInputTarget.style.display = "none"
    }

    // Remove any validation messages
    const validationMessage = this.element.querySelector('.header-validation-message')
    if (validationMessage) {
      validationMessage.remove()
    }
  }

  removeHeader(event) {
    const key = event.currentTarget.dataset.key
    delete this.config.custom_headers[key]
    this.renderHeaders()
    this.updateHiddenField()
  }

  renderUI() {
    this.updateTypeButtons(this.config.request_type)
    this.toggleConfigSections(this.config.request_type)

    if (this.hasMethodSelectTarget) {
      this.methodSelectTarget.value = this.config.http_method
    }
    if (this.hasIntervalSelectTarget) {
      this.intervalSelectTarget.value = this.config.interval
    }
    if (this.hasTimeoutSelectTarget) {
      // Convert milliseconds to seconds for display (select values are in seconds)
      this.timeoutSelectTarget.value = this.config.timeout_ms / 1000
    }
    if (this.hasRedirectCheckTarget) {
      this.redirectCheckTarget.checked = this.config.follow_redirects
    }
    if (this.hasTcpMessageInputTarget) {
      this.tcpMessageInputTarget.value = this.config.tcp_message || ""
    }
    if (this.hasUdpMessageInputTarget) {
      this.udpMessageInputTarget.value = this.config.udp_message || ""
    }

    // DNS fields
    if (this.hasDnsRecordTypeSelectTarget) {
      this.dnsRecordTypeSelectTarget.value = this.config.record_type || this.constructor.DNS_RECORD_TYPE.A
    }
    if (this.hasDnsExpectedValueInputTarget) {
      this.dnsExpectedValueInputTarget.value = this.config.expected_value || ""
    }
    if (this.hasDnsNameserverInputTarget) {
      this.dnsNameserverInputTarget.value = this.config.nameserver || ""
    }
    if (this.hasDnsTimeoutSelectTarget) {
      this.dnsTimeoutSelectTarget.value = this.config.timeout_ms || 5000
    }

    this.renderHeaders()
  }

  updateTypeButtons(selectedType) {
    // Handle Bootstrap tabs (different from button groups)
    this.typeButtonsTarget.querySelectorAll("button.nav-link").forEach(btn => {
      if (btn.dataset.type === selectedType) {
        btn.classList.add("active")
      } else {
        btn.classList.remove("active")
      }
    })

    // Also handle old button group style for backward compatibility
    this.typeButtonsTarget.querySelectorAll("button.btn").forEach(btn => {
      if (btn.dataset.type === selectedType) {
        btn.classList.remove("btn-outline-primary", "btn-outline-secondary")
        btn.classList.add("btn-primary")
      } else {
        btn.classList.remove("btn-primary", "btn-outline-primary")
        btn.classList.add("btn-outline-secondary")
      }
    })
  }

  toggleConfigSections(requestType) {
    // For tab-based UI, Bootstrap handles the show/hide via tab-pane
    // Just update the active tab to match the request type
    // The tab panels are already configured with proper show/hide

    // Show/hide HTTP configuration (for backward compatibility with old UI)
    if (this.hasHttpConfigTarget) {
      const showHttp = ["http", "browser"].includes(requestType)
      this.httpConfigTarget.style.display = showHttp ? "block" : "none"
    }

    // Show/hide TCP configuration (for backward compatibility)
    if (this.hasTcpConfigTarget) {
      this.tcpConfigTarget.style.display = requestType === "tcp" ? "block" : "none"
    }

    // Show/hide UDP configuration (for backward compatibility)
    if (this.hasUdpConfigTarget) {
      this.udpConfigTarget.style.display = requestType === "udp" ? "block" : "none"
    }

    // For SSL and DNS types, all protocol-specific configs are hidden (handled by above logic)
    // SSL and DNS only need interval and timeout, which are always visible
  }

  renderHeaders() {
    if (!this.hasHeadersContainerTarget) return

    // Ensure custom_headers exists and is an object
    if (!this.config.custom_headers || typeof this.config.custom_headers !== 'object') {
      this.config.custom_headers = {}
    }

    let html = ""
    Object.entries(this.config.custom_headers).forEach(([key, value]) => {
      html += `
        <div class="d-flex justify-content-between align-items-center p-2 mb-2 border rounded bg-white">
          <div class="flex-grow-1">
            <div class="row">
              <div class="col-md-6">
                <strong class="text-primary">${this.escapeHtml(key)}</strong>
              </div>
              <div class="col-md-6">
                <span class="text-muted">${this.escapeHtml(value)}</span>
              </div>
            </div>
          </div>
          <button type="button" class="btn btn-sm btn-outline-danger"
                  data-action="click->request-config#removeHeader"
                  data-key="${this.escapeHtml(key)}"
                  title="Remove header"
                  style="min-width: 32px; height: 32px; font-weight: bold;">
            ×
          </button>
        </div>
      `
    })

    if (html === "") {
      html = '<div class="text-muted small mb-2">No custom headers defined</div>'
    }

    this.headersContainerTarget.innerHTML = html
  }

  updateHiddenField() {
    if (this.hasHiddenFieldTarget) {
      this.hiddenFieldTarget.value = JSON.stringify(this.config)
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}